import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useKpros } from '../../context/KprosContext';
import { useTheme } from '../../context/ThemeContext';
import { AlertTriangle, Lightbulb, Check, Loader2, Paperclip, X } from 'lucide-react';
import toast from 'react-hot-toast';

// const SEVERITY_OPTIONS = [
//   { value: 'low', label: 'Low', color: 'bg-green-700 text-gray-200 dark:bg-green-900 dark:text-gray-200' },
//   { value: 'medium', label: 'Medium', color: 'bg-orange-900 text-gray-200 dark:bg-orange-900 dark:text-gray-200' },
//   { value: 'high', label: 'High', color: 'bg-red-700 text-gray-900 dark:bg-red-900 dark:text-gray-200' },
// ];

const CATEGORIES = [
  { value: 'discoveries', label: 'Discovery' },
  { value: 'prompt_tips', label: 'Prompt Tip' },
  { value: 'workarounds', label: 'Workaround' },
  { value: 'general', label: 'General' },
];

const FEATURE_TAGS = [
  { value: '', label: 'General (no specific feature)' },
  { value: 'studio', label: 'Studio' },
  { value: 'ait', label: 'AIT' },
  { value: 'data_extraction', label: 'Data Extraction' },
  { value: 'profitability', label: 'JPRO Analyzer' },
  { value: 'team_chat', label: 'Team Chat' },
  { value: 'email', label: 'Email' },
];

export default function KprosReportForm() {
  const { reportIssue, submitTip } = useKpros();
  const { theme } = useTheme();
  const location = useLocation();

  const [mode, setMode] = useState('issue'); // 'issue' or 'tip'
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Issue fields
  const [issueTitle, setIssueTitle] = useState('');
  const [issueDescription, setIssueDescription] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [attachments, setAttachments] = useState([]); // [{file, preview}]

  // Tip fields
  const [tipTitle, setTipTitle] = useState('');
  const [tipBody, setTipBody] = useState('');
  const [tipCategory, setTipCategory] = useState('discoveries');
  const [tipFeature, setTipFeature] = useState('');

  const resetForm = () => {
    setIssueTitle(''); setIssueDescription(''); setSeverity('medium'); setAttachments([]);
    setTipTitle(''); setTipBody(''); setTipCategory('discoveries'); setTipFeature('');
    setSubmitted(false);
  };

  const handleSubmitIssue = async (e) => {
    e.preventDefault();
    if (!issueTitle.trim() || !issueDescription.trim()) return;
    setLoading(true);
    const success = await reportIssue({
      title: issueTitle,
      description: issueDescription,
      severity,
      page_url: location.pathname,
      attachments: attachments.map(a => a.file),
    });
    setLoading(false);
    if (success) {
      setSubmitted(true);
      toast.success('Issue reported successfully');
    } else {
      toast.error('Failed to report issue');
    }
  };

  const handleSubmitTip = async (e) => {
    e.preventDefault();
    if (!tipTitle.trim() || !tipBody.trim()) return;
    setLoading(true);
    const success = await submitTip({
      title: tipTitle,
      body: tipBody,
      category: tipCategory,
      feature_tag: tipFeature || null,
    });
    setLoading(false);
    if (success) {
      setSubmitted(true);
      toast.success('Tip submitted for review');
    } else {
      toast.error('Failed to submit tip');
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-3">
          <Check size={24} className="text-green-600 dark:text-green-400" />
        </div>
        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">
          {mode === 'issue' ? 'Issue Reported' : 'Tip Submitted'}
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          {mode === 'issue' ? 'An email has been sent to the admin team.' : 'Your tip will be reviewed by an admin.'}
        </p>
        <button
          onClick={resetForm}
          className="text-xs text-blue-500 hover:text-blue-600"
        >
          Submit another
        </button>
      </div>
    );
  }

  const inputClass = `w-full text-sm px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500`;

  return (
    <div className="flex flex-col h-full">
      {/* Mode toggle */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <button
          onClick={() => { setMode('issue'); setSubmitted(false); }}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
            mode === 'issue'
              ? 'text-red-600 dark:text-red-400 border-b-2 border-red-500'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
          }`}
        >
          <AlertTriangle size={13} />
          Report Issue
        </button>
        <button
          onClick={() => { setMode('tip'); setSubmitted(false); }}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
            mode === 'tip'
              ? 'text-orange-600 dark:text-orange-400 border-b-2 border-orange-500'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
          }`}
        >
          <Lightbulb size={13} />
          Share a Tip
        </button>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-y-auto p-3">
        {mode === 'issue' ? (
          <form onSubmit={handleSubmitIssue} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Title</label>
              <input type="text" value={issueTitle} onChange={e => setIssueTitle(e.target.value)} className={inputClass} placeholder="Brief description of the issue" required />
            </div>
            <div>
              {/* <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Severity</label> */}
              {/* <div className="flex gap-2">
                {SEVERITY_OPTIONS.map(s => (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => setSeverity(s.value)}
                    className={`flex-1 py-1.5 rounded text-xs font-medium transition-colors ${
                      severity === s.value ? s.color : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div> */}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Description</label>
              <textarea value={issueDescription} onChange={e => setIssueDescription(e.target.value)} className={inputClass} rows={5} placeholder="What happened? What did you expect?" required />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Attachments (optional)</label>
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {attachments.map((a, i) => (
                    <div key={i} className="relative group">
                      {a.preview ? (
                        <img src={a.preview} alt="" className="w-16 h-16 rounded-lg object-cover border border-gray-200 dark:border-gray-600" />
                      ) : (
                        <div className="w-16 h-16 rounded-lg border border-gray-200 dark:border-gray-600 flex items-center justify-center text-[10px] text-gray-400 px-1 text-center truncate">
                          {a.file.name}
                        </div>
                      )}
                      <button type="button" onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))}
                        className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <label className="inline-flex items-center gap-1.5 text-xs text-blue-500 hover:text-blue-600 cursor-pointer">
                <Paperclip size={12} /> Add screenshot or file
                <input type="file" className="hidden" accept="image/*,.pdf,.doc,.docx,.txt,.csv,.xlsx" multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    const newAttachments = files.map(f => ({
                      file: f,
                      preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
                    }));
                    setAttachments(prev => [...prev, ...newAttachments]);
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
            <div className="text-xs text-gray-400 dark:text-gray-500">
              Page: {location.pathname}
            </div>
            <button
              type="submit"
              disabled={loading || !issueTitle.trim() || !issueDescription.trim()}
              className="mx-auto px-4 py-2 bg-red-800 text-center text-white text-sm rounded-lg hover:bg-red-400 disabled:opacity-90 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <AlertTriangle size={14} />}
              Report Issue
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmitTip} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Title</label>
              <input type="text" value={tipTitle} onChange={e => setTipTitle(e.target.value)} className={inputClass} placeholder="e.g. 'Better Studio results with...'" required />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Category</label>
              <select value={tipCategory} onChange={e => setTipCategory(e.target.value)} className={inputClass}>
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Feature (optional)</label>
              <select value={tipFeature} onChange={e => setTipFeature(e.target.value)} className={inputClass}>
                {FEATURE_TAGS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Your tip or discovery</label>
              <textarea value={tipBody} onChange={e => setTipBody(e.target.value)} className={inputClass} rows={5} placeholder="Share what you found or a tip that might help others..." required />
            </div>
            <button
              type="submit"
              disabled={loading || !tipTitle.trim() || !tipBody.trim()}
              className="mx-auto py-2 px-4 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-900 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Lightbulb size={14} />}
              Submit Tip
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
